import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Button,
    TextField,
    InputAdornment,
} from "@mui/material";
import CommonModal from "../../../components/common/CommonModal";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../../../components/common/EnhancedTable";
import { useFeeTransaction } from "../../../hooks/useFeeTransaction";
import { useError } from "../../../contexts/ErrorContext";

const FeeTransactionsManagement: React.FC = () => {
    const { selectedSession, selectedClass } = useSessionClass();
    const { t } = useTranslation();
    const { showError } = useError();

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { data, isLoading, error } = useFeeTransaction(
        selectedClass?.id,
        selectedSession?.id
    );

    const getTimeValue = (date: any) => {
        if (!date) return 0;
        if (Array.isArray(date)) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = date;
            return new Date(year, month - 1, day, hour, minute, second).getTime();
        }
        return new Date(date).getTime();
    };

    const formatDate = (date: any) => {
        if (!date) return "N/A";
        if (Array.isArray(date)) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = date;
            return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
        }
        return new Date(date).toLocaleString();
    };

    const tableData = useMemo(() => {
        if (!data) return [];

        // Sort data by recent date first
        const sortedData = [...data].sort((a, b) => {
            const dateA = getTimeValue(a.localDateTime || (a as any).createdAt);
            const dateB = getTimeValue(b.localDateTime || (b as any).createdAt);
            return dateB - dateA;
        });

        return sortedData.map((transaction) => ({
            ...transaction,
            date: formatDate(transaction.localDateTime || (transaction as any).createdAt),
        }));
    }, [data]);

    const handleViewTransaction = (transaction: any) => {
        setSelectedTransaction(transaction);
        setViewDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setViewDialogOpen(false);
        setSelectedTransaction(null);
    };

    const columns = [
        {
            id: "studentAccountNumber",
            label: t("account_number"),
            minWidth: 150,
            format: (_: any, row: any) => row.studentAccountsResponseDto?.studentAccountNumber || row.studentAccountNo || "-"
        },
        {
            id: "transactionId",
            label: t("transaction_no"),
            minWidth: 150
        },
        {
            id: "transactionType",
            label: t("transaction_type"),
            minWidth: 150,
            format: (value: string) => {
                if (value === "CREDIT") return t("credit");
                if (value === "DEBIT") return t("debit");
                if (value === "YEARLY_FEE") return t("yearly_fee");
                if (value === "ADMINISTRATOR_UPDATED") return t("administrator_updated");
                return value || "-";
            }
        },
        {
            id: "paymentType",
            label: t("payment_type"),
            minWidth: 120
        },
        {
            id: "amount",
            label: t("amount"),
            minWidth: 100,
            align: "right" as const,
            format: (value: number) => `₹${value?.toLocaleString() || "0"}`
        },
        {
            id: "view",
            label: t("view"),
            minWidth: 100,
            align: "center" as const,
            format: (_: any, row: any) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewTransaction(row);
                    }}
                    sx={{
                        textTransform: "none",
                        borderRadius: 10,
                        boxShadow: "none",
                        bgcolor: "#3092e3",
                        px: 2,
                        "&:hover": {
                            boxShadow: "none",
                            bgcolor: "primary.main",
                        }
                    }}
                >
                    {t("view")}
                </Button>
            )
        },
    ];

    const isActuallyLoading = isLoading && !!selectedClass?.id && !!selectedSession?.id;

    if (isActuallyLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t("error_loading_transactions")}</Alert>
            </Box>
        );
    }
    return (
        <>
            <Paper sx={{ p: 1.5, maxWidth: 1500, mx: 'auto', mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                            fontSize: "1.25rem",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("fee_transactions")}
                    </Typography>
                    <TextField
                        size="small"
                        placeholder={t("search")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
                    />
                </Box>

                {(!selectedSession || !selectedClass) && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                        {t("please_select_session_and_class_for_transactions") || "Please select a session and class"}
                    </Alert>
                )}
                <EnhancedTable
                    columns={columns}
                    data={tableData}
                    searchFields={["studentAccountsResponseDto.studentAccountNumber", "studentAccountNo", "transactionId", "paymentType", "transactionType"]}
                    hideSearch
                    externalSearchTerm={searchTerm}
                    cardProps={{
                        elevation: 0,
                        sx: {
                            border: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            mb: 0,
                            '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
                        },
                    }}
                />
            </Paper>

            <CommonModal
                open={viewDialogOpen}
                onClose={handleCloseDialog}
                title={t("transaction_details")}
                maxWidth="sm"
                fullWidth
                hideActions
                contentSx={{ p: 2 }}
            >
                {selectedTransaction && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography variant="body2">
                            <strong>{t("account_number")}:</strong> {selectedTransaction.studentAccountsResponseDto?.studentAccountNumber || selectedTransaction.studentAccountNo || "-"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("transaction_no")}:</strong> {selectedTransaction.transactionId || "-"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("transaction_type")}:</strong>{" "}
                            {(() => {
                                const value = selectedTransaction.transactionType;
                                if (value === "CREDIT") return t("credit");
                                if (value === "DEBIT") return t("debit");
                                if (value === "YEARLY_FEE") return t("yearly_fee");
                                if (value === "ADMINISTRATOR_UPDATED") return t("administrator_updated");
                                return value || "-";
                            })()}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("payment_type")}:</strong> {selectedTransaction.paymentType || "-"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("amount")}:</strong>{" "}
                            {`₹${(selectedTransaction.amount ?? 0).toLocaleString()}`}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("date")}:</strong> {selectedTransaction.date || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("remark")}:</strong> {selectedTransaction.remark || selectedTransaction.remarks || "-"}
                        </Typography>
                    </Box>
                )}
            </CommonModal>
        </>
    );
};

export default FeeTransactionsManagement;
